export interface AvatarIdentity {
  slug?: string | null;
  agent_slug?: string | null;
  is_system?: boolean;
}

export interface AvatarAssignmentIdentity {
  name?: string | null;
  slug?: string | null;
  customer_name?: string | null;
  customer_slug?: string | null;
}

export interface AvatarEditorValues {
  name?: string;
  slug?: string;
  description?: string;
  role_label?: string;
  provider_avatar_id?: string;
  portrait_url?: string;
  system_prompt?: string;
  behavior_prompt?: string;
  status?: string;
  customer_ids?: string[];
}

export interface AvatarCustomerSelectionState {
  selectedCount: number;
  totalCount: number;
  isAllSelected: boolean;
  isPartiallySelected: boolean;
}

export interface LiveAvatarCatalogItem {
  id: string;
  name?: string | null;
  type?: string | null;
  preview_url?: string | null;
  source?: string | null;
  default_voice?: string | null;
  is_green_screen?: boolean | null;
}

const uniqueNonEmptyIds = (ids: string[]): string[] => Array.from(new Set(
  ids.map(id => String(id || '').trim()).filter(Boolean),
));

export const avatarProfileFingerprint = (values: AvatarEditorValues): string => JSON.stringify({
  name: values.name || '',
  slug: values.slug || '',
  description: values.description || '',
  role_label: values.role_label || '',
  provider_avatar_id: values.provider_avatar_id || '',
  portrait_url: values.portrait_url || '',
  system_prompt: values.system_prompt || '',
  behavior_prompt: values.behavior_prompt || '',
  status: values.status || 'draft',
});

export const avatarAssignmentFingerprint = (values: AvatarEditorValues): string =>
  JSON.stringify([...(values.customer_ids || [])].sort());

export const getAvatarCustomerSelectionState = (
  customerIds: string[],
  selectedCustomerIds: string[],
): AvatarCustomerSelectionState => {
  const eligibleIds = uniqueNonEmptyIds(customerIds);
  const selectedIds = new Set(uniqueNonEmptyIds(selectedCustomerIds));
  const selectedCount = eligibleIds.filter(id => selectedIds.has(id)).length;
  const totalCount = eligibleIds.length;

  return {
    selectedCount,
    totalCount,
    isAllSelected: totalCount > 0 && selectedCount === totalCount,
    isPartiallySelected: selectedCount > 0 && selectedCount < totalCount,
  };
};

export const toggleAllAvatarCustomers = (
  customerIds: string[],
  selectedCustomerIds: string[],
): string[] => {
  const eligibleIds = uniqueNonEmptyIds(customerIds);
  const eligibleIdSet = new Set(eligibleIds);
  const currentIds = uniqueNonEmptyIds(selectedCustomerIds);
  const { isAllSelected } = getAvatarCustomerSelectionState(eligibleIds, currentIds);

  if (isAllSelected) {
    return currentIds.filter(id => !eligibleIdSet.has(id));
  }

  return uniqueNonEmptyIds([...currentIds, ...eligibleIds]);
};

export const filterLiveAvatarCatalog = <T extends LiveAvatarCatalogItem>(
  avatars: T[],
  search: string,
): T[] => {
  const selectableAvatars = avatars.filter(avatar => avatar.is_green_screen !== true);
  const needle = search.trim().toLocaleLowerCase('de');
  if (!needle) return selectableAvatars;
  return selectableAvatars.filter(avatar => [avatar.name, avatar.id, avatar.type, avatar.default_voice]
    .some(value => String(value || '').toLocaleLowerCase('de').includes(needle)));
};

export const liveAvatarSelectionValues = (
  avatar: LiveAvatarCatalogItem,
): Pick<AvatarEditorValues, 'provider_avatar_id' | 'portrait_url'> => ({
  provider_avatar_id: String(avatar.id || '').trim(),
  portrait_url: String(avatar.preview_url || '').trim(),
});

export const isLiveAvatarCatalogSelection = (
  avatar: LiveAvatarCatalogItem,
  providerAvatarId: string | null | undefined,
): boolean => String(avatar.id || '').trim() === String(providerAvatarId || '').trim();

export const isSallyAvatar = (profile: AvatarIdentity): boolean =>
  profile.slug === 'sally' || profile.is_system === true;

export const buildAvatarLaunchUrl = (
  customerSlug: string,
  profile: AvatarIdentity,
): string => {
  const agentSlug = String(profile.agent_slug || profile.slug || 'sally').trim() || 'sally';
  return `/agent/${encodeURIComponent(customerSlug)}/${encodeURIComponent(agentSlug)}?voice=live`;
};

export const requiresAvatarProfileResolution = (
  routeAgentSlug?: string | null,
  search = '',
): boolean => {
  const agentSlug = String(routeAgentSlug || 'sally').trim().toLowerCase() || 'sally';
  const requestedAvatar = String(new URLSearchParams(search).get('avatar') || '').trim().toLowerCase();
  return agentSlug !== 'sally' || Boolean(requestedAvatar && requestedAvatar !== 'sally');
};

export const buildCanonicalAgentUrl = (
  customerSlug: string,
  agentSlug: string,
  search: string,
): string => {
  const params = new URLSearchParams(search);
  params.delete('avatar');
  const query = params.toString();
  return `/agent/${encodeURIComponent(customerSlug)}/${encodeURIComponent(agentSlug)}${query ? `?${query}` : ''}`;
};

export const isAgentAdminScopeReady = (
  confirmedRouteScopeKey: string,
  routeCustomerSlug: string,
  routeAgentSlug: string,
  activeCustomerSlug?: string | null,
  activeAgentSlug?: string | null,
): boolean => confirmedRouteScopeKey === `${routeCustomerSlug}/${routeAgentSlug}`
  && activeCustomerSlug === routeCustomerSlug
  && (activeAgentSlug || 'sally') === routeAgentSlug;

export const getAvatarPlaceholderCount = (
  availableAvatarCount: number,
  minimumCardCount = 4,
): number => Math.max(0, minimumCardCount - Math.max(0, availableAvatarCount));

export const formatAvatarAssignmentSummary = (
  profile: AvatarIdentity & { assigned_customers?: AvatarAssignmentIdentity[] | null },
  visibleNameCount = 2,
): string => {
  if (isSallyAvatar(profile)) return 'Alle Kunden';
  const labels = (profile.assigned_customers || [])
    .map(customer => customer.name || customer.customer_name || customer.slug || customer.customer_slug || '')
    .map(label => label.trim())
    .filter(Boolean);
  if (labels.length === 0) return 'Keine Kunden';
  const visible = labels.slice(0, Math.max(1, visibleNameCount));
  const remaining = labels.length - visible.length;
  return `${visible.join(', ')}${remaining > 0 ? ` +${remaining}` : ''}`;
};
